import React, { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import DropdownMenu from '../../../../components/dropdown-menu/DropdownMenu';
import MediaPreview from '../../../../components/media-preview/MediaPreview';
import DateDisplay, { DATE_DISPLAY_TYPE } from '../../../../components/date-display/DateDisplay';
import ToolTip from '../../../../components/tooltip/ToolTip';
import { Shot, getShotPlaybackSrc, hasValidDeliveryDate, useUpdateShotStatusAnimatedMutation } from '../../../../app/services/shots';
import { ShotStatus } from '../../../../types/home';
import { isProducerGroupPosition } from '../../../../utils/memberPositionUtils';
import useAnimatedStatusChange from '../../../../hooks/useAnimatedStatusChange';

interface WaitingShotCardProps {
  shot: Shot;
  project_user_position: string;
  onShotStatusChanged?: () => void;
}

const WaitingShotCard: React.FC<WaitingShotCardProps> = ({
  shot,
  project_user_position,
  onShotStatusChanged,
}) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { isMobile } = useSelector((state: any) => state.device);
  const [updateShotStatus] = useUpdateShotStatusAnimatedMutation();
  const shotId = shot.id;

  const showDeliveryDate = useMemo(() => hasValidDeliveryDate(shot), [shot.next_delivery_date]);
  const showProducerMenu = isProducerGroupPosition(project_user_position);

  const { fadingOut, executeWithAnimation } = useAnimatedStatusChange({
    onComplete: onShotStatusChanged,
  });

  const handlePreviewClick = useCallback(() => {
    if (projectId && shotId) {
      navigate(`/${projectId}/${shotId}`, {
        state: { shot }
      });
    }
  }, [navigate, projectId, shot, shotId]);

  const handleSetHold = useCallback(() => {
    if (!projectId || !shotId) return;
    executeWithAnimation(() =>
      updateShotStatus({
        project_id: projectId,
        shot_id: shotId,
        status: ShotStatus.PRODUCER_DONE_CLIENT_UNAPPROVED,
      }).unwrap()
    );
  }, [projectId, shotId, updateShotStatus, executeWithAnimation]);

  const handleSetProducerTask = useCallback(() => {
    if (!projectId || !shotId) return;
    executeWithAnimation(() =>
      updateShotStatus({
        project_id: projectId,
        shot_id: shotId,
        status: ShotStatus.PRODUCER_PENDING,
      }).unwrap()
    );
  }, [projectId, shotId, updateShotStatus, executeWithAnimation]);

  const producerMenuItems = useMemo(() => ([
    {
      label: '自分のタスクに戻す',
      icon: 'shortcut',
      onClick: handleSetProducerTask,
    },
    {
      label: 'このショットを保留',
      icon: 'do_not_disturb_on',
      onClick: handleSetHold,
    },
  ]), [handleSetProducerTask, handleSetHold]);

  return (
    <div className={`u-col${fadingOut ? ' fade-out-bck' : ''}`} style={fadingOut ? { pointerEvents: 'none' } : undefined}>
      <ToolTip description={shot.shot_name} placement="bottom">
        <div onClick={handlePreviewClick} style={{ cursor: 'pointer' }}>
          <MediaPreview
            src={getShotPlaybackSrc(shot) || shot.thumbnail || undefined}
            thumbnailSrc={shot.thumbnail || undefined}
            sizeMode={{ mode: 'fixed-height', height: '96px' }}
            autoPlay={false}
            hoverPlay={!isMobile}
            disableHoverEffect={true}
            controls={false}
            showStatusBar={false}
            showWaveform={false}
            className="video-wrapper"
          />
        </div>
      </ToolTip>

      <div className="u-row u-items-center u-w100" style={{ marginTop: '-2px' }}>
        {!isMobile && (
          <div className="u-text-light-gray bodytext-11">
            {showDeliveryDate ? (
              <DateDisplay date={shot.next_delivery_date!} type={DATE_DISPLAY_TYPE.DELIVERY_SCHEDULE} />
            ) : (
              <DateDisplay date={shot.updated_at} type={DATE_DISPLAY_TYPE.RELATIVE_DAYS} />
            )}
          </div>
        )}
        {showProducerMenu && (
          <div onClick={(e) => e.stopPropagation()} style={{ marginLeft: 'auto' }}>
            <DropdownMenu
              items={producerMenuItems}
              placement="top-end"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default WaitingShotCard;

